import React, { useMemo, useState } from 'react';
import { Box, InputAdornment, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import { formatAge, formatDate, ScrollTable, TierChip, ToneChip } from './consoleUi';

/**
 * Lowercased search haystack for a tenant.
 * @param {object} tenant - Tenant row.
 * @returns {string} Text.
 */
const haystack = (tenant) =>
  [tenant.name, tenant.domain, tenant.id, ...(tenant.adminEmails || []), ...(tenant.planNames || [])].join(' ').toLowerCase();

/**
 * Filter tenants by query and tier filter.
 * @param {object[]} tenants - Rows.
 * @param {string} query - Search text.
 * @param {'all'|'paid'|'free'} filter - Tier filter.
 * @returns {object[]} Matching rows.
 */
export function filterTenants(tenants, query, filter) {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  return (tenants || []).filter((tenant) => {
    if (filter === 'paid' && tenant.tier === 'free') return false;
    if (filter === 'free' && tenant.tier !== 'free') return false;
    if (!words.length) return true;
    const text = haystack(tenant);
    return words.every((word) => text.includes(word));
  });
}

/**
 * Tenants tab: searchable table; selecting a row opens the tenant drawer.
 * @param {object} props - Props.
 * @param {object[]} props.tenants - Tenant rows.
 * @param {(tenant: object) => void} props.onSelect - Open a tenant.
 * @returns {JSX.Element} Panel.
 */
export default function ConsoleTenants({ tenants, onSelect }) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const rows = useMemo(() => filterTenants(tenants, query, filter), [tenants, query, filter]);
  const now = Date.now();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search name, domain, admin email or id"
          inputProps={{ 'aria-label': 'Search tenants' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ flex: '1 1 260px', minWidth: 0 }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_, value) => value && setFilter(value)}
          aria-label="Filter by tier"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, fontSize: 13 } }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="paid">Paid</ToggleButton>
          <ToggleButton value="free">Free</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" sx={{ color: 'var(--q-muted, #5E5A54)', ml: 'auto' }} aria-live="polite">
          {rows.length === (tenants || []).length ? `${rows.length} tenants` : `${rows.length} of ${(tenants || []).length} tenants`}
        </Typography>
      </Box>

      <ScrollTable label="Tenants" minWidth={860} maxHeight="calc(var(--q-main-height, 100vh) - 280px)">
        <TableHead>
          <TableRow>
            <TableCell>Tenant</TableCell>
            <TableCell>Tier</TableCell>
            <TableCell align="right">Members</TableCell>
            <TableCell>Admins</TableCell>
            <TableCell>Stripe</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Last active</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center', color: 'var(--q-muted, #5E5A54)' }}>
                No tenants match.
              </TableCell>
            </TableRow>
          )}
          {rows.map((tenant) => (
            <TableRow
              key={tenant.id}
              hover
              onClick={() => onSelect(tenant)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell sx={{ maxWidth: 260 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelect(tenant);
                  }}
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'block',
                    maxWidth: '100%',
                    borderRadius: 'var(--q-radius-xs, 4px)',
                    '&:focus-visible': { outline: '2px solid var(--q-focus-ring, #4F3BD6)', outlineOffset: 2 },
                  }}
                >
                  <Typography component="span" noWrap title={tenant.name} sx={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--q-text, #1C1B19)' }}>
                    {tenant.name}
                  </Typography>
                  <Typography component="span" noWrap sx={{ display: 'block', fontSize: 12, color: 'var(--q-faint, #716C65)' }}>
                    {tenant.domain || (tenant.isPersonal ? 'Personal workspace' : 'No domain')}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell><TierChip tier={tenant.tier} /></TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{tenant.memberCount}</TableCell>
              <TableCell sx={{ maxWidth: 220 }}>
                <Typography component="span" noWrap title={(tenant.adminEmails || []).join(', ')} sx={{ display: 'block', fontSize: 13 }}>
                  {(tenant.adminEmails || []).length ? tenant.adminEmails.join(', ') : 'None'}
                </Typography>
              </TableCell>
              <TableCell>
                {tenant.stripe?.hasSubscription ? (
                  <ToneChip tone="success">Subscribed</ToneChip>
                ) : tenant.stripe?.hasCustomer ? (
                  <ToneChip tone="warning">Customer</ToneChip>
                ) : (
                  <Typography component="span" sx={{ fontSize: 12.5, color: 'var(--q-faint, #716C65)' }}>None</Typography>
                )}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(tenant.createdAt, 'Unknown')}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }} title={formatDate(tenant.lastActivityAt)}>{formatAge(tenant.lastActivityAt, now)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </ScrollTable>
    </Box>
  );
}
